import { html, LitElement, PropertyValues, svg, TemplateResult } from 'lit';
import { state, property, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { styles } from '../view/styles';
import { GzCounterWebComponent } from '../../gzCounter';

export class GzGaugeWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({
    type: Number,
    reflect: true,
  })
  value = 0;

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  disabled = false;

  @property({
    type: Boolean,
    reflect: true,
  })
  animations = true;

  @property({
    type: Boolean,
    reflect: true,
  })
  hasLabels = false;

  @property({
    type: String,
    reflect: true,
  })
  percentageUnit = '%';

  /*
   * The colors property is used to define the colors for the gauge. Must be added 3 colors.
   */
  @property({ type: Array, attribute: true, reflect: true })
  colors: string[];

  /*
   * The gradientOffsets property is used to define the gradient stops for the gauge.
   * The default values are 0%, 50%, and 100%.
   */
  @property({ type: Array, attribute: true, reflect: true })
  gradientOffsets: number[] = [0, 50, 100];

  @query('#counterRef')
  counterElement: GzCounterWebComponent;

  @property({ type: Object })
  markerStyles = {
    transform: 'rotate(0deg)',
  };

  @state()
  rootStyles;

  @state()
  gaugeColors: Array<string>;

  // https://lit-element.polymer-project.org/api/modules/_lit_element_.html#internalproperty
  @state()
  /** The SVG stroke-dasharray property controls the pattern of dashes and
   *  gaps on the outline of a shape.
   *  https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-dasharray
   */
  protected dashArray = 142;

  @state()
  /** The SVG stroke-dashoffset controls the offset of the dash-array.
   * We use this property to create the gauge-filling animation
   * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-dashoffset
   * */
  protected dashOffset = 142;

  @state()
  private indicatorRotation = 0;

  protected readonly labelsFillColor = 'var(--body-text-arrows-400)';
  protected readonly labelsFontFamily = 'IBMPlex Mono, sans-serif';
  protected readonly labelsFontSize = '0.3em';
  protected readonly labelsData = [
    { x: -7, y: 50, text: '0' },
    { x: 8, y: 14, text: '25' },
    { x: 47, y: -1, text: '50' },
    { x: 84, y: 14, text: '75' },
    { x: 100, y: 49, text: '100' },
  ];

  constructor() {
    super();
    this.rootStyles = getComputedStyle(document.body);
  }

  static get styles() {
    return styles;
  }

  protected renderGaugeValue(): TemplateResult {
    return this.disabled
      ? html`<span class="display-1 disabled">--</span>`
      : html` <gz-counter id="counterRef" target="${this.value}" ?animations="${this.animations}"></gz-counter
          ><span>${this.percentageUnit ? this.percentageUnit : ''}</span>`;
  }

  updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('value') || changedProperties.has('disabled')) {
      if (this.animations) {
        this.updateProgress(0);

        setTimeout(() => {
          this.updateProgress(!this.disabled ? this.value : 0);
        }, 800);
      } else {
        this.updateProgress(this.value);
      }
    }
  }

  render(): TemplateResult {
    this.setGaugeColors();

    return html`
      <div class="gauge">
        <div class="gauge--wrapper">
          <div class="gauge--labels-container">
            <span class="step step--1"></span>
            <span class="step step--2"></span>
            <span class="step step--3"></span>
          </div>
          <div class="gauge--value">${this.renderGaugeValue()}</div>
          ${this.hasLabels ? this.renderGraphWithLabels() : this.renderGraph()}
        </div>
      </div>
    `;
  }

  protected setGaugeColors(): void {
    const defaultColors = ['--yellow-alert-050', '--orange-alert-chart-100', '--red-chart-600'];
    const colorsToProcess = this.colors?.length === 3 ? this.colors : defaultColors;

    this.gaugeColors = colorsToProcess.map(color =>
      color.startsWith('--') ? this.rootStyles.getPropertyValue(color).trim() : color
    );
  }

  protected renderGraphWithLabels(): TemplateResult {
    return html`
      <svg class="outer-wrapper" viewBox="-6.7 -5 120 55" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="gradient" y1="71.67" x2="143.21" y2="71.67" gradientUnits="userSpaceOnUse">
            <stop offset=${this.gradientOffsets[0] + '%'} stop-color="${this.gaugeColors[0]}" />
            <stop offset=${this.gradientOffsets[1] + '%'} stop-color="${this.gaugeColors[1]}" />
            <stop offset=${this.gradientOffsets[2] + '%'} stop-color="${this.gaugeColors[2]}" />
          </linearGradient>
        </defs>
        <g fill-opacity="0" stroke-width="10">
          <path class="outer" d="M5 50a45 45 0 1 1 90 0" stroke-width="6" />
          <path
            id="gauge"
            class="gauge-progress"
            d="M5 50a45 45 0 1 1 90 0"
            stroke-width="6"
            stroke-dasharray="${this.dashArray}"
            stroke-dashoffset="${this.dashOffset}"
            stroke="url(#gradient)"
          />
        </g>
        <g fill-opacity="1">${this.renderGraphLabels()}</g>
      </svg>
    `;
  }

  protected renderGraphLabels(): TemplateResult {
    return svg`
      ${this.labelsData.map(
        label =>
          svg`
            <text
                x="${label.x}"
                y="${label.y}"
                fill="${this.labelsFillColor}"
                font-family="${this.labelsFontFamily}"
                font-size="${this.labelsFontSize}">
              ${label.text}${this.percentageUnit ? this.percentageUnit : ''}
            </text>
          `
      )}
    `;
  }

  protected renderGraph(): TemplateResult {
    return html`
      <svg
        style=${styleMap(this.markerStyles)}
        class="marker"
        viewBox="0 1.5 100 50"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <marker id="dot" viewBox="0 0 10 10" refX="4" refY="4" markerWidth="3" markerHeight="3">
            <circle cx="4" cy="4" r="4" fill="white" />
          </marker>
        </defs>
        <g>
          <line
            id="marker"
            x1="5"
            y1="50"
            x2="5"
            y2="50"
            stroke="none"
            stroke-width="1"
            marker-start="url(#dot)"
          ></line>
        </g>
      </svg>
      <svg class="outer-wrapper" viewBox="0 0 100 50" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="gradient" y1="71.67" x2="143.21" y2="71.67" gradientUnits="userSpaceOnUse">
            <stop offset=${this.gradientOffsets[0] + '%'} stop-color="${this.gaugeColors[0]}" />
            <stop offset=${this.gradientOffsets[1] + '%'} stop-color="${this.gaugeColors[1]}" />
            <stop offset=${this.gradientOffsets[2] + '%'} stop-color="${this.gaugeColors[2]}" />
          </linearGradient>
        </defs>
        <g fill-opacity="0" stroke-width="10">
          <path class="outer" d="M5 50a45 45 0 1 1 90 0" stroke-width="4" />
          <path
            id="gauge"
            class="gauge-progress"
            d="M5 50a45 45 0 1 1 90 0"
            stroke-width="4"
            stroke-dasharray="${this.dashArray}"
            stroke-dashoffset="${this.dashOffset}"
            stroke="url(#gradient)"
          />
        </g>
      </svg>
    `;
  }

  private updateGaugeAnimation(): void {
    /** We are using 180 (a half circle angle of rotation) to calculate the
     *  marker position on the gauge.
     *  However, at a full rotation, when the value = 100, the marker falls
     *  below the gauge. In order to make the marker dot stop just above the
     *  baseline, we need to set a smaller value for the angle of rotation.
     *  In this case a value of 175 is optimal to display the dot correctly.
     */
    if (this.disabled) {
      this.indicatorRotation = 0;
      this.markerStyles = {
        transform: `rotate(${this.indicatorRotation}deg)`,
      };
      return;
    }

    const marker_rotation_offset = 5; // offset to adjust the marker position

    if (this.value > 0 && this.value < 100) {
      this.indicatorRotation = (this.value / 100) * 180 - marker_rotation_offset;
    } else if (this.value > 100) {
      /** we need the indicator to stop at the end of the gauge if by any
       * chance the value will be greater than 100
       */
      this.indicatorRotation = 175;
    } else {
      this.indicatorRotation = this.value <= 0 ? 0 : (this.value / 100) * 175;
    }

    this.markerStyles = {
      transform: `rotate(${this.indicatorRotation}deg)`,
    };
  }

  private updateProgress(value: number): void {
    if (value >= 100) {
      this.dashArray = 140;
      // show the progress at 100 if the value is > 100;
      this.dashOffset = 0;
    } else if (value >= 99) {
      // Special handling for values between 99 and 100 to make the transition visible
      this.dashArray = 142;
      this.dashOffset = 4.3;
    } else if (value > 0) {
      this.dashArray = 142;
      this.dashOffset = value === 1 ? 141.9 : this.dashArray - (value * this.dashArray) / 100 + 2;
    } else {
      // reset the properties if the value falls back to 0
      this.dashArray = 142;
      this.dashOffset = 142;
    }

    this.updateGaugeAnimation();
  }
}
